import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { TransactionReportResponse, TransType, PaymentStatus } from "../models/Transactions";
import { MerchantTariff } from "../models/MerchantModel";
import { storageService } from "./StorageService";
import { authService } from "./authService";
import { ApiPagedResult, PagedResult, Result } from "../models/Result";
import { CountryModel } from "../models/CountryModel";
import { RESIDENCE_PERMIT_REQUEST_TIMEOUT_MS } from "../models/ResidencePermit";
import EventEmitter, { Events } from "./eventemitter";
import { SearchRequestBaseModel } from "../models/SearchRequestModel";

class HttpService {
    private instance: AxiosInstance;
    private baseUrl: string = process.env.REACT_APP_CRM_API_BASE_URL ?? process.env.REACT_APP_API_BASE_URL ?? "";
    private baseApiUrl: string = `${this.baseUrl}`;

    constructor() {
        this.instance = axios.create({
            baseURL: this.baseApiUrl,
            timeout: 120000,
        } as AxiosRequestConfig);

        // Grab the token from storage and put it into the http
        const accessToken = storageService.getAccessToken();
        if (accessToken.length > 0) {
            this.setAuthToken(accessToken);
        }

        let isLoggingOut = false; // shared flag

        this.instance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const status = error?.response?.status;

                if (status === 401) {
                    // If logout process already started, just reject
                    if (isLoggingOut) {
                        return Promise.reject(error);
                    }

                    isLoggingOut = true; // prevent duplicate logouts

                    try {
                        const user = await authService.getUser();

                        if (user && !user.expired) {
                            // Token seems valid but API says no — log out
                            EventEmitter.emit(Events.onLogout, "401");
                            return Promise.reject(error);
                        }

                        // Try silent refresh
                        await authService.signinSilent();

                        // Retry original request
                        isLoggingOut = false; // allow future logouts again
                        return this.instance.request(error.config);
                    } catch (refreshError) {
                        // Silent refresh failed — log out immediately
                        EventEmitter.emit(Events.onLogout, "401");
                        return Promise.reject(error);
                    }
                }

                return Promise.reject(error);
            },
        );
    }

    login = async (): Promise<void> => {
        await authService.signinRedirect();
    };

    setAuthToken = (token: string) => {
        this.instance.defaults.headers.common["Authorization"] = token;
    };

    getCountries = async (): Promise<Result<CountryModel[]>> => {
        const countries = await this.get<CountryModel[]>("/country");
        countries.data.sort((a, b) => (a.name > b.name ? 1 : -1));

        return countries;
    };

    // Only the EU/EEA members (TNPDEVAPPS-1954) — the Issuing Country options of a residence permit.
    // The residence permit editor is its only user, so it gets that editor's short deadline and a
    // stuck CRM ends in the Retry under Issuing Country.
    getEuEeaCountries = async (): Promise<Result<CountryModel[]>> => {
        const countries = await this.get<CountryModel[]>("/country/eu-eea", { timeout: RESIDENCE_PERMIT_REQUEST_TIMEOUT_MS });
        // A failed request carries no data; sorting it would throw and hide CRM's message.
        if (countries.success && countries.data) {
            countries.data.sort((a, b) => (a.name > b.name ? 1 : -1));
        }

        return countries;
    };

    get = async <T>(path: string, config?: AxiosRequestConfig<string>): Promise<Result<T>> => {
        return this.send(() => this.instance.get<T>(path, config));
    };

    getPage = async <T>(path: string, data: SearchRequestBaseModel, config?: AxiosRequestConfig<string>): Promise<PagedResult<T>> => {
        if (data.pageNumber == undefined || data.pageSize == undefined) {
            return new Promise((resolve, reject) => {
                resolve({ data: null!, success: false, message: "Page Number or Page Size is Not Valid.", totalCount: 0 });
            });
        }

        return this.sendPaged(() => this.instance.post<ApiPagedResult<T>>(path, data, config));
    };

    delete = async <T>(path: string, config?: AxiosRequestConfig<string>,data?:any): Promise<Result<T>> => {
        return this.send(() => this.instance.delete<T>(path,{...config,data}));
    };

    post = async <T>(path: string, data: any, config?: AxiosRequestConfig<string>): Promise<Result<T>> => {
        return this.send(() => this.instance.post<T>(path, data, config));
    };

    put = async <T>(path: string, data: any, config?: AxiosRequestConfig<string>): Promise<Result<T>> => {
        return this.send(() => this.instance.put<T>(path, data, config));
    };

    // For endpoints that return raw bytes on success (e.g. a File(...) action result). Needs its own
    // path, not send(): with responseType "arraybuffer" a JSON error body also arrives as raw bytes,
    // so it has to be decoded back to text/JSON before getErrorMessagesFromRequestBody can read it —
    // otherwise every failure would surface as an unhelpful stringified ArrayBuffer.
    getBinary = async (path: string, config?: AxiosRequestConfig<string>): Promise<Result<ArrayBuffer>> => {
        try {
            const promiseResult = await this.instance.get<ArrayBuffer>(path, { ...config, responseType: "arraybuffer" });

            return {
                success: promiseResult.status >= 200 && promiseResult.status <= 299,
                data: promiseResult.data,
                message: "",
                status: promiseResult.status,
            };
        } catch (ex) {
            const exception = ex as AxiosError;
            let errorBody: any = exception?.response?.data;
            if (errorBody instanceof ArrayBuffer) {
                try {
                    errorBody = JSON.parse(new TextDecoder().decode(errorBody));
                } catch {
                    errorBody = null;
                }
            }

            return {
                success: false,
                data: null!,
                message: this.getErrorMessagesFromRequestBody(errorBody, exception.message),
                status: exception?.response?.status,
            } as Result<ArrayBuffer>;
        }
    };

    send = async <T>(promise: () => Promise<AxiosResponse<T, any>>): Promise<Result<T>> => {
        try {
            const promiseResult = await promise();

            const result: Result<T> = {
                success: promiseResult.status >= 200 && promiseResult.status <= 299,
                data: promiseResult.data,
                message: this.getErrorMessagesFromRequestBody(promiseResult.data, ""),
                status: promiseResult.status,
            };

            return result;
        } catch (ex) {
            const exception = ex as AxiosError;

            return {
                success: false,
                data: null!,
                message: this.getErrorMessagesFromRequestBody(exception?.response?.data, exception.message),
                status: exception?.response?.status,
            } as Result<T>;
        }
    };

    sendPaged = async <T>(promise: () => Promise<AxiosResponse<ApiPagedResult<T>, any>>): Promise<PagedResult<T>> => {
        try {
            const promiseResult = await promise();

            const result: PagedResult<T> = {
                success: promiseResult.status >= 200 && promiseResult.status <= 299,
                data: promiseResult.data.data,
                message: this.getErrorMessagesFromRequestBody(promiseResult.data.data, ""),
                totalCount: promiseResult.data.totalCount,
            };

            return result;
        } catch (ex) {
            const exception = ex as AxiosError;

            return {
                success: false,
                data: null!,
                message: this.getErrorMessagesFromRequestBody(exception?.response?.data, exception.message),
                totalCount: 0,
            } as PagedResult<T>;
        }
    };

    getErrorMessagesFromRequestBody = (data: any, message?: string) => {
        if (data == null) {
            return message ?? "";
        }
        const internalServerErrorCode = 500;

        if (data && data.status == internalServerErrorCode) {
            return data.title;
        }

        let finalResult = data;
        if (data.errors) {
            finalResult = data.errors;

            if (finalResult.Error) {
                finalResult = finalResult.Error;
            }

            // Handling nested error objects and string-encoded errors
            if (Array.isArray(finalResult)) {
                finalResult = finalResult
                    .map((error) => {
                        if (typeof error === "string") {
                            try {
                                const parsedError = JSON.parse(error);
                                if (parsedError.errors && parsedError.errors.Error) {
                                    return parsedError.errors.Error.join("; ");
                                }
                            } catch (e) {
                                return error;
                            }
                        }
                        return error;
                    })
                    .join("; ");
            }
        }

        // Trim Quotes. TODO. Move to extensions
        return JSON.stringify(finalResult).replace(/^"+/, "").replace(/"+$/, "");
    };

    getMerchantTariffsById = (id: string): MerchantTariff[] => {
        return [
            { id: "1", name: "Tariff 1", groupNames: ["G1", "G2"], totalUsers: 5, details: [], code: "T1", merchantName: "Merchant 1" },
            { id: "2", name: "Tariff 2", groupNames: ["G2", "G3"], totalUsers: 5, details: [], code: "T2", merchantName: "Merchant 1" },
            { id: "3", name: "Tariff 3", groupNames: ["G1", "G3"], totalUsers: 5, details: [], code: "T3", merchantName: "Merchant 1" },
        ];
    };
}

const httpService = new HttpService();

export { httpService };
